import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Card } from "@mantine/core";
import { getPublishedAppointments } from "../../data/actions/appointmentActions";
import { IState } from "../../data/reducers/state";
import { AppointmentStatus } from "../../types/appointments/AppointmentStatus";
import { AppointmentType } from "../../types/appointments/AppointmentType";
import {
  IPublishedAppointment,
  IPublishedAppointmentDetail,
  IPublishedGameAppointmentDetails,
  IPublishedSupervisorAppointmentDetails,
} from "../../types/appointments/PublishedAppointment";
import { IAssociation } from "../../types/association/Association";
import { IVenue } from "../../types/venue/Venue";
import { toDateTime, toDateTimeNullable } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";
import UpcomingGameRow from "./UpcomingGameRow";
import UpcomingSupervisorRow from "./UpcomingSupervisorRow";

export interface IUpcomingAppointmentsProps {
  venues: { [key: string]: IVenue };
  actions: {
    getPublishedAppointments: () => Promise<
      IPublishedAppointment<IPublishedAppointmentDetail>[]
    >;
  };
  relevantAssociations: IAssociation[];
}

export interface IUpcomingAppointmentsState {
  byDay?: { [day: string]: string[] };
  appointmentMap?: {
    [appointmentId: string]: IPublishedAppointment<IPublishedAppointmentDetail>;
  };
}

// const UpcomingGamesCard: React.FunctionComponent<{games: {[day: string]: IGameAppointment[]}, text:}> = ({games, text)}) => {

// }

const formatDate = (date: DateTime | null) => {
  if (!date) return "";
  if (date.year == DateTime.local().year) {
    return (
      <p>
        <b>{date.toFormat("cccc")}</b> -{date.toFormat("d LLLL")}
      </p>
    );
  }
  return (
    <p>
      <b>{date.toFormat("cccc")}</b> -{date.toFormat("d LLLL yyyy")}
    </p>
  );
};

interface IUpcomingAppointmentsBlockProps {
  name: string;
  emptyText: string;
  days: any;
  dayMap: any;
  appointmentsMap: {
    [key: string]: IPublishedAppointment<IPublishedAppointmentDetail>;
  };
  venues: { [key: string]: IVenue };
  updateAppointmentStatus: Function;
  infoButton?: any | null;
}

const UpcomingRowForAppointment: React.FunctionComponent<{
  appointment: IPublishedAppointment<any>;
  updateAppointmentStatus;
  venue: IVenue;
}> = ({ appointment, updateAppointmentStatus, venue }) => {
  switch (appointment.type) {
    case AppointmentType.game:
      return (
        <UpcomingGameRow
          key={appointment._id}
          appointment={
            appointment as IPublishedAppointment<IPublishedGameAppointmentDetails>
          }
          venue={venue}
          didUpdateStatus={(status: AppointmentStatus) =>
            updateAppointmentStatus(appointment._id, status)
          }
        />
      );

    case AppointmentType.supervisor:
      return (
        <UpcomingSupervisorRow
          key={appointment._id}
          appointment={
            appointment as IPublishedAppointment<IPublishedGameAppointmentDetails>
          }
          venue={venue}
          didUpdateStatus={(status: AppointmentStatus) =>
            updateAppointmentStatus(appointment._id, status)
          }
        />
      );
  }

  return (
    <p>
      Unsupported appointment type
      {appointment.type}
    </p>
  );
};

const UpcomingAppointmentsBlock: React.FunctionComponent<
  IUpcomingAppointmentsBlockProps
> = ({
  name,
  emptyText,
  days,
  dayMap,
  appointmentsMap,
  venues,
  updateAppointmentStatus,
  infoButton,
}) => (
    <Card padding="md">
      <p
        style={{ marginBottom: "15px", fontSize: "18px", marginRight: "5px" }}
      >
        <b>{name}</b>
      </p>
      {days.length === 0 && (
        <p style={{ color: "darkgray", margin: "auto", textAlign: "center" }}>
          {emptyText}
        </p>
      )}
      {days.map((day, idx) => {
        const gs: IPublishedAppointment<IPublishedAppointmentDetail>[] = dayMap[
          day
        ].map((ap) => appointmentsMap[ap]);
        // const sortedAppointments = _.orderBy(gs, g => toDateTimeNullable(g.date), "asc")

        // @ts-ignore
        const venue: IVenue | null = venues[gs[0].details.venueId || ""];
        // @ts-ignore
        const isExternal = gs?.[0]?.details?.isExternalVenue ?? false;
        const highlightColor = isExternal ? "#ffffbb" : "";

        const locationName = venue ? venue.name : "";
        const tz = venue?.tz;
        const date = toDateTime(gs[0].start, tz);

        return (
          <div key={`${name}-child-${date?.toISO() || idx}`}>
            {idx !== 0 && <hr />}
            <h6>{formatDate(date)}</h6>
            <p style={{ color: "gray", backgroundColor: highlightColor }}>
              {locationName}
            </p>
            {gs.map((apt, idx) => (
              <UpcomingRowForAppointment
                key={`upcoming-row-for-appointment-${apt._id}`}
                appointment={apt}
                updateAppointmentStatus={updateAppointmentStatus}
                venue={venue}
              />
            ))}
          </div>
        );
      })}
    </Card>
  );

const UpcomingAppointments: React.FunctionComponent<
  IUpcomingAppointmentsProps
> = ({ venues, actions, relevantAssociations }) => {
  const [byDay, setByDay] = React.useState<
    { [day: string]: string[] } | undefined
  >(undefined);
  const [appointmentMap, setAppointmentMap] = React.useState<
    | {
      [
      appointmentId: string
      ]: IPublishedAppointment<IPublishedAppointmentDetail>;
    }
    | undefined
  >(undefined);

  const updateAppointmentStatus = (
    appointmentId,
    status: AppointmentStatus,
  ) => {
    setAppointmentMap({
      ...appointmentMap,
      [appointmentId]: {
        ...(appointmentMap || {})[appointmentId],
        status,
      },
    });
  };

  React.useEffect(() => {
    actions.getPublishedAppointments().then((appointments) => {
      const byAppointment = _.keyBy(appointments, "_id");
      const groupedInitial: { [day: string]: string[] } = appointments.reduce(
        (prev, apt) => {
          let key =
            toDateTime(apt.start)?.toFormat("yyyyLLDD") || apt.start.toString();

          if (apt.type == AppointmentType.game) {
            const d = apt.details as IPublishedGameAppointmentDetails;
            key += `-${d.venueId}`;
          } else if (apt.type == AppointmentType.supervisor) {
            const d = apt.details as IPublishedSupervisorAppointmentDetails;
            key += `-${d.venueId || ""}`;
          } else {
            // skip for now
            return prev;
          }
          prev[key] = key in prev ? prev[key].concat([apt._id]) : [apt._id]
          return prev
        },
        {},
      );
      const grouped = {}
      Object.keys(groupedInitial).forEach(key => {
        const sorted = _.orderBy(groupedInitial[key], (a) => toDateTimeNullable(byAppointment[a]?.start))
        grouped[key] = sorted
      })

      setAppointmentMap(byAppointment);
      setByDay(grouped);
    });
  }, []);

  const games = byDay || {};
  const apMap = appointmentMap || {};

  if (!byDay) return <CenteredLoader />;
  // order the days
  const orderedDayKeys = _.orderBy(
    Object.keys(games),
    (a) => toDateTimeNullable(apMap[games[a][0]].start),
    "asc",
  );
  const startOfToday: DateTime = DateTime.local().startOf("day");
  const past = orderedDayKeys.filter(
    (day) => toDateTime(apMap[games[day][0]].start) < startOfToday,
  );
  const upcoming = orderedDayKeys.filter(
    (day) => toDateTime(apMap[games[day][0]].start) >= startOfToday,
  );

  let incorrectPaymentButton: any;

  const associationsWithPaymentButtons = relevantAssociations.filter(
    (f) => f.config?.paymentDisputeLink,
  );

  if (associationsWithPaymentButtons.length > 0) {
    const style = {
      color: "darkgray",
      margin: "5px 0 10px auto",
      padding: "0",
      // float: "right",
      fontSize: "10px",
    };
    if (relevantAssociations.length == 1) {
      incorrectPaymentButton = [
        <a
          style={style}
          href={associationsWithPaymentButtons[0].config.paymentDisputeLink}
          rel="noopener noreferrer"
          target="_blank"
        >
          Report incorrect payment
        </a>,
      ];
    } else {
      // prepare multiple buttons with association name on it
      incorrectPaymentButton = associationsWithPaymentButtons.map(
        (association, idx) => (
          <a
            key={`paymentDispute-${association._id}`}
            style={style}
            href={association.config.paymentDisputeLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            Report {association.name}
            's payment
          </a>
        ),
      );
    }
  }

  return (
    <div>
      <UpcomingAppointmentsBlock
        name="Upcoming appointments"
        emptyText="You don't have any upcoming appointments"
        days={upcoming}
        dayMap={games}
        appointmentsMap={apMap}
        venues={venues}
        updateAppointmentStatus={updateAppointmentStatus}
      />

      <br />

      <UpcomingAppointmentsBlock
        name="Previous appointments"
        emptyText="You're up to date, great job!"
        days={past}
        dayMap={games}
        appointmentsMap={apMap}
        venues={venues}
        updateAppointmentStatus={updateAppointmentStatus}
      />
      {incorrectPaymentButton && (
        <div
          style={{
            maxWidth: "400px",
            width: "100%",
            textAlign: "right",
          }}
        >
          {incorrectPaymentButton}
        </div>
      )}
    </div>
  );
};

const mapState2Props = (state: IState) => {
  const associationIds = new Set(
    state.persons.map[state.persons.self]?.associations?.map(
      (a) => a.associationId,
    ) || [],
  );
  return {
    venues: state.venues.map,
    relevantAssociations: Array.from(associationIds)
      .map((a) => state.associations.map[a])
      .filter((a) => !!a),
  };
};

function mapDispatchToProps(dispatch): Partial<IUpcomingAppointmentsProps> {
  return {
    actions: {
      getPublishedAppointments: () => dispatch(getPublishedAppointments()),
    },
  };
}

export default connect(
  mapState2Props,
  mapDispatchToProps,
)(UpcomingAppointments);
